/**
 * ChatMessage - Single chat message with inline citation markers
 *
 * Key feature: Uses citationSupports from Gemini to render [1], [2] markers
 * at the exact positions in the text where they're referenced.
 */

import { useMemo } from 'react';
import { KnowledgeCitation, KnowledgeCitationSupport } from '@deep-agents/shared-types';
import { CitationMarker } from './CitationMarker';

// Icons
const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const BotIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);

// Type for a parsed message segment
export type ParsedSegment =
  | { type: 'text'; content: string; highlighted?: boolean; citationIndex?: number }
  | { type: 'citation'; indices: number[] };

export interface ParsedMessage {
  segments: ParsedSegment[];
}

/**
 * Find the next sentence-ending position after a given index.
 * Looks for . ! ? followed by space or end of text.
 */
function findSentenceEnd(content: string, startPos: number): number {
  // Look for sentence-ending punctuation after startPos
  for (let i = startPos; i < content.length; i++) {
    const char = content[i];
    if (char === '.' || char === '!' || char === '?') {
      // Check if this is likely a sentence end (followed by space, newline, or end)
      const nextChar = content[i + 1];
      if (!nextChar || nextChar === ' ' || nextChar === '\n' || nextChar === '\r') {
        return i + 1; // Position after the punctuation
      }
    }
  }
  // If no sentence end found, return the original position
  return startPos;
}

/**
 * Insert citation markers into the text at positions specified by citationSupports.
 * Citations are placed at the end of sentences for natural reading flow.
 * Text spans that are cited get highlighted for visual clarity.
 */
function insertCitationsIntoText(
  content: string,
  citationSupports?: KnowledgeCitationSupport[]
): ParsedSegment[] {
  if (!citationSupports || citationSupports.length === 0) {
    // No citation positions - just return the text as-is
    return [{ type: 'text', content }];
  }

  // Build a list of all boundaries (start/end of highlights + citation markers)
  type Boundary =
    | { pos: number; type: 'highlight_start'; citationIndex: number }
    | { pos: number; type: 'highlight_end'; citationIndex: number }
    | { pos: number; type: 'citation_marker'; indices: number[] };

  const boundaries: Boundary[] = [];
  const citationsByPosition = new Map<number, number[]>();

  for (const support of citationSupports) {
    // Add highlight boundaries for the cited text span
    if (support.startIndex < support.endIndex) {
      boundaries.push({ pos: support.startIndex, type: 'highlight_start', citationIndex: support.citationIndex });
      boundaries.push({ pos: support.endIndex, type: 'highlight_end', citationIndex: support.citationIndex });
    }

    // Find the end of the sentence for placing the citation marker
    const sentenceEnd = findSentenceEnd(content, support.endIndex);
    if (!citationsByPosition.has(sentenceEnd)) {
      citationsByPosition.set(sentenceEnd, []);
    }
    const indices = citationsByPosition.get(sentenceEnd)!;
    if (!indices.includes(support.citationIndex)) {
      indices.push(support.citationIndex);
    }
  }

  // Add citation markers to boundaries
  for (const [pos, indices] of citationsByPosition) {
    boundaries.push({ pos, type: 'citation_marker', indices: indices.sort((a, b) => a - b) });
  }

  // Sort boundaries by position
  boundaries.sort((a, b) => a.pos - b.pos);

  // Track which citations are currently active (for highlighting)
  const activeHighlights = new Set<number>();
  const segments: ParsedSegment[] = [];
  let lastIndex = 0;

  for (const boundary of boundaries) {
    // Add text segment before this boundary
    if (boundary.pos > lastIndex) {
      const textContent = content.slice(lastIndex, boundary.pos);
      if (textContent) {
        const isHighlighted = activeHighlights.size > 0;
        const citationIndex = isHighlighted ? Math.min(...activeHighlights) : undefined;
        segments.push({
          type: 'text',
          content: textContent,
          highlighted: isHighlighted,
          citationIndex,
        });
      }
    }

    // Process the boundary
    if (boundary.type === 'highlight_start') {
      activeHighlights.add(boundary.citationIndex);
    } else if (boundary.type === 'highlight_end') {
      activeHighlights.delete(boundary.citationIndex);
    } else if (boundary.type === 'citation_marker') {
      segments.push({
        type: 'citation',
        indices: boundary.indices,
      });
    }

    lastIndex = boundary.pos;
  }

  // Add remaining text after last boundary
  if (lastIndex < content.length) {
    const textContent = content.slice(lastIndex);
    if (textContent) {
      segments.push({
        type: 'text',
        content: textContent,
        highlighted: activeHighlights.size > 0,
      });
    }
  }

  return segments;
}

/**
 * Fallback: Parse existing [1], [2] markers if citationSupports not available.
 */
function parseExistingCitationMarkers(content: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  const citationRegex = /\[(\d+)\]/g;

  let lastIndex = 0;
  let match;

  while ((match = citationRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, match.index),
      });
    }

    segments.push({
      type: 'citation',
      indices: [parseInt(match[1], 10)],
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex),
    });
  }

  // If no citations found, return full text
  if (segments.length === 0) {
    return [{ type: 'text', content }];
  }

  return segments;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: KnowledgeCitation[];
  /** Citation positions from Gemini groundingSupports */
  citationSupports?: KnowledgeCitationSupport[];
  timestamp: Date;
}

// Action icons for message footer
const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const ThumbsUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const ThumbsDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
  </svg>
);

interface ChatMessageProps {
  message: Message;
  onCitationClick?: (documentId: string) => void;
  onSaveAsNote?: (content: string) => void;
}

export function ChatMessage({ message, onCitationClick, onSaveAsNote }: ChatMessageProps) {
  const isUser = message.role === 'user';

  // Parse the message content to find citation markers
  // Priority: Use citationSupports (from Gemini) if available, else fallback to regex parsing
  const segments = useMemo(() => {
    if (message.citationSupports && message.citationSupports.length > 0) {
      // Use Gemini's groundingSupports for precise positioning
      return insertCitationsIntoText(message.content, message.citationSupports);
    }
    // Fallback: parse [1], [2] markers from the text itself
    return parseExistingCitationMarkers(message.content);
  }, [message.content, message.citationSupports]);

  // Map citation index to citation data
  const citationMap = useMemo(() => {
    const map: Record<number, KnowledgeCitation> = {};
    if (message.citations) {
      message.citations.forEach((citation, i) => {
        // Citations are 1-indexed in the text
        map[i + 1] = citation;
      });
    }
    return map;
  }, [message.citations]);

  // Map citation index to the text it supports (from the answer)
  const citedTextMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (message.citationSupports && message.content) {
      for (const support of message.citationSupports) {
        const citedText = message.content.slice(support.startIndex, support.endIndex);
        // If multiple supports for same citation, keep the first one
        if (!map[support.citationIndex] && citedText) {
          map[support.citationIndex] = citedText.trim();
        }
      }
    }
    return map;
  }, [message.citationSupports, message.content]);

  // Copy message to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
  };

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      {/* User messages: compact bubble style */}
      {isUser ? (
        <>
          <div className="message-avatar user-avatar">
            <UserIcon />
          </div>
          <div className="message-content">
            <div className="user-bubble">
              {message.content}
            </div>
          </div>
        </>
      ) : (
        /* Assistant messages: prose style like NotebookLM */
        <div className="assistant-response">
          <div className="prose-text">
            {segments.map((segment, segmentIndex) => {
              if (segment.type === 'text') {
                // Apply highlighting to cited text spans
                if (segment.highlighted) {
                  return (
                    <span
                      key={segmentIndex}
                      className={`cited-text cited-text-${segment.citationIndex}`}
                      title={`Supported by source [${segment.citationIndex}]`}
                    >
                      {segment.content}
                    </span>
                  );
                }
                return <span key={segmentIndex}>{segment.content}</span>;
              }

              // Citation markers - can have multiple indices at one position like [1][2]
              return (
                <span key={segmentIndex} className="citation-group">
                  {segment.indices.map((citationIndex) => {
                    const citation = citationMap[citationIndex];
                    const citedText = citedTextMap[citationIndex];
                    return (
                      <CitationMarker
                        key={citationIndex}
                        index={citationIndex}
                        citation={citation}
                        citedText={citedText}
                        onClick={() => {
                          if (citation && onCitationClick) {
                            onCitationClick(citation.documentId);
                          }
                        }}
                      />
                    );
                  })}
                </span>
              );
            })}
          </div>

          {/* Action buttons for assistant messages */}
          <div className="message-actions">
            <button
              type="button"
              className="action-btn"
              onClick={() => onSaveAsNote?.(message.content)}
              title="Save to Note"
            >
              <SaveIcon />
              <span>Save to Note</span>
            </button>
            <button type="button" className="action-btn icon-only" onClick={handleCopy} title="Copy">
              <CopyIcon />
            </button>
            <button type="button" className="action-btn icon-only" title="Good response">
              <ThumbsUpIcon />
            </button>
            <button type="button" className="action-btn icon-only" title="Bad response">
              <ThumbsDownIcon />
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* === DARK THEME CHAT MESSAGES === */
        .chat-message {
          margin-bottom: 24px;
          position: relative;
        }

        /* === USER MESSAGES: Compact bubble === */
        .chat-message.user {
          display: flex;
          gap: 12px;
          flex-direction: row-reverse;
        }

        .user-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: rgba(99, 102, 241, 0.2);
          color: #818cf8;
        }

        .message-content {
          max-width: 80%;
        }

        .user-bubble {
          padding: 10px 14px;
          background: rgba(99, 102, 241, 0.15);
          border: 1px solid rgba(99, 102, 241, 0.25);
          border-radius: 16px;
          border-bottom-right-radius: 4px;
          font-size: 14px;
          line-height: 1.5;
          color: rgba(226, 232, 240, 0.95);
        }

        /* === ASSISTANT MESSAGES: Prose style like NotebookLM === */
        .chat-message.assistant {
          display: block;
        }

        .assistant-response {
          width: 100%;
        }

        .prose-text {
          font-size: 14px;
          line-height: 1.75;
          color: rgba(226, 232, 240, 0.9);
          white-space: pre-wrap;
          word-break: break-word;
        }

        /* Paragraph-like styling for prose */
        .prose-text strong,
        .prose-text b {
          font-weight: 600;
          color: #fff;
        }

        /* === MESSAGE ACTIONS === */
        .message-actions {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 8px;
          font-size: 12px;
          color: rgba(148, 163, 184, 0.7);
          cursor: pointer;
          transition: all 0.15s;
        }

        .action-btn:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.1);
          color: rgba(226, 232, 240, 0.9);
        }

        .action-btn.icon-only {
          padding: 6px 8px;
        }

        .action-btn.icon-only span {
          display: none;
        }

        /* === CITATION STYLING === */
        .citation-group {
          display: inline;
        }

        /* Highlighted cited text - subtle underline to show grounded claims */
        .cited-text {
          background: linear-gradient(to bottom, transparent 85%, rgba(99, 102, 241, 0.3) 85%);
          border-radius: 2px;
          transition: background 0.15s ease;
        }

        .cited-text:hover {
          background: rgba(99, 102, 241, 0.15);
        }

        /* Different colors for different citation indices */
        .cited-text-1 { background: linear-gradient(to bottom, transparent 85%, rgba(99, 102, 241, 0.35) 85%); }
        .cited-text-2 { background: linear-gradient(to bottom, transparent 85%, rgba(59, 130, 246, 0.35) 85%); }
        .cited-text-3 { background: linear-gradient(to bottom, transparent 85%, rgba(16, 185, 129, 0.35) 85%); }
        .cited-text-4 { background: linear-gradient(to bottom, transparent 85%, rgba(245, 158, 11, 0.35) 85%); }
        .cited-text-5 { background: linear-gradient(to bottom, transparent 85%, rgba(239, 68, 68, 0.35) 85%); }

        .cited-text-1:hover { background: rgba(99, 102, 241, 0.2); }
        .cited-text-2:hover { background: rgba(59, 130, 246, 0.2); }
        .cited-text-3:hover { background: rgba(16, 185, 129, 0.2); }
        .cited-text-4:hover { background: rgba(245, 158, 11, 0.2); }
        .cited-text-5:hover { background: rgba(239, 68, 68, 0.2); }
      `}</style>
    </div>
  );
}
